// WeeklySupervisionForSupervisor.tsx
import React, { useState, useEffect } from "react";
import {
    completeAndRolloverSupervision,
    getCurrentWeeklySupervisionByEmployee,
    getHistoricalWeeklySupervisionByEmployee,
    getSupervisorCadence,
    postSupervisionComment,
    SupervisionCadenceApiResponse,
    SupervisionCadenceDto,
    SupervisionStatus,
    SupervisionWeek,
} from "../../../services/weeklySupervision/weeklySupervisionService";
import { useAuth } from "../../../firebase/AuthProvider";
import Modal from "../calendar/events/Model";
import { toast } from "react-toastify";
import AddWeeklySupervisionMeeting from "./AddWeeklySupervisionMeeting";
import { CommentModal } from "./CommentModal";
import "./weeklySupervision.css";
import Loader from "../loader/Loader";
import { PaginationDto } from "../../../interfaces/Employee/Employee";
import './WeeklySupervisionForSupervisor.css';
// import SupervisorHeader from "./supervisor/SupervisorHeader"; // Optional based on design
// import SuperviseeCadenceTable from "./supervisor/SuperviseeCadenceTable"; // REMOVED
import SupervisorTaskList from "./supervisor/SupervisorTaskList";
import Pagination from "../../ui/Pagination/Pagination";
import RescheduleMeetingModal from "./RescheduleMeetingModal";
import moment from "moment";
import { GetEmployeeProfileDetailsAsync } from "../../../services/EmployeeService/EmployeeService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import EmployeeCarousel from "../../ui/EmployeeCarousel/EmployeeCarousel";

const WeeklySupervisionForSupervisor: React.FC = () => {
    const [cadenceData, setCadenceData] = useState<SupervisionCadenceDto[]>([]);
    const [selectedSC, setSelectedSC] = useState<SupervisionCadenceDto | null>(null);
    const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);
    const [meetingDetails, setMeetingDetails] = useState<{ id: string; startTime: string; endTime: string; description: string; } | null>(null);
    const [taskList, setTaskList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isTasksLoading, setIsTasksLoading] = useState(false); // Separate loader for tasks
    const [error, setError] = useState<string | null>(null);
    const { currentUser } = useAuth();
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [showMeetingForm, setShowMeetingForm] = useState(false);
    const [showRescheduleMeetingForm, setShowRescheduleMeetingForm] = useState(false);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [currentComment, setCurrentComment] = useState<string>("");
    const [editingRecordIndex, setEditingRecordIndex] = useState<number | null>(null);
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 50 });
    const [count, setCount] = useState<number>();
    const [selectedSubType, setSelectedSubType] = useState("");

    // Search State
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    // Debounce Effect
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            if (searchTerm !== debouncedSearchTerm) {
                setPagination(prev => ({ ...prev, pageNumber: 1 }));
            }
        }, 500);

        return () => clearTimeout(timerId);
    }, [searchTerm]);

    // 1. Fetch Cadence Data (The Carousel Items)
    useEffect(() => {
        async function fetchCadenceData() {
            if (!currentUser) return;
            setIsLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const rawCadenceData: SupervisionCadenceApiResponse[] = await getSupervisorCadence(token, currentUser.uid);
                
                // Enrich with names
                const enrichedData = await Promise.all(
                    rawCadenceData.map(async (cadence) => {
                        const profileResponse = await GetEmployeeProfileDetailsAsync(token, cadence.scId);
                        if (profileResponse.success) {
                            return {
                                ...cadence,
                                scFirstName: profileResponse.data.firstName,
                                scLastName: profileResponse.data.lastName,
                                scUserId: cadence.scId,
                            };
                        }
                        return {
                            ...cadence,
                            scFirstName: 'Unknown', scLastName: 'User', scUserId: cadence.scId,
                        };
                    })
                );
                
                setCadenceData(enrichedData);

                // Optional: Auto-select the first Supervisor if none selected?
                // if (enrichedData.length > 0 && !selectedSC) handleSCSelect(enrichedData[0]);

            } catch (err: any) {
                console.error("Error fetching cadence data:", err);
                setError(err.message || "Unable to load supervisee data.");
            } finally {
                setIsLoading(false);
            }
        }
        fetchCadenceData();
    }, [currentUser, fetchTrigger]);

    // 2. Fetch Tasks (When a Supervisor is selected in Carousel)
    useEffect(() => {
        const fetchSupervisionList = async () => {
            if (!selectedSC || !currentUser) {
                setTaskList([]);
                setCount(undefined);
                setMeetingDetails(null);
                return;
            }

            setIsTasksLoading(true); // Local loading state for the task section
            try {
                const token = await currentUser.getIdToken();
                let response;

                if (selectedInstanceId) {
                    response = await getHistoricalWeeklySupervisionByEmployee(
                        token,
                        selectedInstanceId,
                        selectedSC.id,
                        selectedSubType,
                        pagination.pageNumber,
                        pagination.pageSize,
                        debouncedSearchTerm
                    );
                } else {
                    response = await getCurrentWeeklySupervisionByEmployee(
                        token,
                        selectedSC.id,
                        selectedSubType,
                        pagination.pageNumber,
                        pagination.pageSize,
                        debouncedSearchTerm
                    );
                }

                setTaskList(response.tasks);
                setCount(response.count);

                if (response.supervisionInstance?.meetingDetails?.startTime) {
                    const details = response.supervisionInstance.meetingDetails;
                    setMeetingDetails({ ...details, description: details.description || '' });
                } else {
                    setMeetingDetails(null);
                }
            } catch (err) {
                console.error("Error fetching supervision list:", err);
                setTaskList([]);
                setCount(undefined);
                setMeetingDetails(null);
            } finally {
                setIsTasksLoading(false);
            }
        };
        fetchSupervisionList();
    }, [selectedSC, selectedInstanceId, pagination, selectedSubType, currentUser, debouncedSearchTerm]);

    // Handler for Carousel Selection
    const handleSCSelect = (sc: SupervisionCadenceDto) => {
        if (selectedSC?.scId === sc.scId) return; // No change
        setSelectedSC(sc);
        setSelectedInstanceId(null);
        setPagination({ pageNumber: 1, pageSize: 50 });
        setSearchTerm("");
    };

    // Keep existing handlers
    const handleCompleteAndRollover = async () => { /* ... existing ... */ }
    const handleSaveComment = async () => { /* ... existing ... */ }
    const handleEditComment = (index: number) => { /* ... existing ... */ }
    const handleCancelComment = () => { /* ... existing ... */ }
    const onPageChange = (pageNum: number) => setPagination((prev) => ({ ...prev, pageNumber: pageNum }));
    const resetPageSize = (pageNumber: number, pageSize: number) => setPagination({ pageNumber, pageSize });

    if (isLoading && !cadenceData.length) return <Loader />;
    if (error) return <div className="alert alert-danger">Error: {error}</div>;

    const instanceIdForModals = selectedInstanceId ?? selectedSC?.history.find(h => h.status === "IN PROGRESS")?.instanceId;

    return (
        <>
            <div className="weekly-supervision-breadcrumb">
                <Link to="/dashboard" className="breadcrumb-link">Dashboard</Link>
                <span className="breadcrumb-separator">{">"}</span>
                <span className="breadcrumb-active">Weekly Supervision</span>
            </div>

            <div className="weekly-supervision-container">
                
                {/* --- 1. EMPLOYEE CAROUSEL --- */}
                {/* This replaces the old table row selection */}
                <div style={{ marginBottom: '2rem' }}>
                    <EmployeeCarousel 
                        cadenceData={cadenceData}
                        selectedSC={selectedSC}
                        onSelect={handleSCSelect}
                    />
                </div>

                {/* --- 2. SEARCH & HEADER --- */}
                {/* Only show search if a user is selected, or keep it global if desired */}
                <div className="weekly-supervision-top-section" style={{ marginBottom: '1rem' }}>
                    <div className="weekly-supervision-search-container">
                        <div className="weekly-supervision-search">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by participant..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                                disabled={!selectedSC} 
                            />
                        </div>
                    </div>

                    {meetingDetails && (
                        <div className="weekly-supervision-notification">
                            <strong>Meeting Scheduled:</strong> Your weekly supervision meeting is set for{' '}
                            {moment(meetingDetails.startTime).format('MMM D, YYYY, h:mm A')}.
                        </div>
                    )}
                </div>

                {/* --- 3. MAIN CONTENT AREA --- */}
                {selectedSC ? (
                    <div className="participant-view-container fade-in">
                        {/* Header for the selected view */}
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-700">
                                Participants for {selectedSC.firstName} {selectedSC.lastName}
                            </h3>
                            {/* You can add the meeting buttons here if you removed SupervisorHeader */}
                        </div>

                        {/* THE PARTICIPANT LIST (Accordion Dropdowns) */}
                        {/* This component already groups by participantName, creating the "Participant Dropdowns" you wanted */}
                        <SupervisorTaskList
                            taskList={taskList}
                            selectedSC={selectedSC}
                            handleEditComment={handleEditComment}
                            isLoading={isTasksLoading}
                        />

                        {/* PAGINATION */}
                        {!isTasksLoading && typeof count === 'number' && count > 0 && (
                            <div style={{ marginTop: '1.5rem' }}>
                                <Pagination
                                    currentPage={pagination.pageNumber}
                                    totalPages={Math.ceil(count / pagination.pageSize)}
                                    onPageChange={onPageChange}
                                    pageSize={pagination.pageSize}
                                    resetPageSize={resetPageSize}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    /* --- EMPTY STATE --- */
                    <div className="empty-selection-state">
                        <div className="text-center p-10 bg-white rounded-lg border border-gray-200 shadow-sm">
                            <h4 className="text-xl font-medium text-gray-600 mb-2">Select a Support Coordinator</h4>
                            <p className="text-gray-400">Click a card in the carousel above to view their participants and tasks.</p>
                        </div>
                    </div>
                )}

                {/* --- MODALS --- */}
                <CommentModal
                    isOpenComment={isCommentModalOpen}
                    onClose={handleCancelComment}
                    onSave={handleSaveComment}
                    comment={currentComment}
                    setComment={setCurrentComment}
                />
                 {/* Add your meeting modals back here if needed */}
            </div>
        </>
    );
};
export default WeeklySupervisionForSupervisor;